import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { firstValueFrom, of } from 'rxjs';
import { catchError, filter, take, timeout } from 'rxjs/operators';
import { LoggedInUserSettings } from 'src/app/core/services';
import { PlanDetail } from 'src/app/model/plan/plan-detail';

const PLAN_DETAILS_TIMEOUT_MS = 10000;

/**
 * Redirects /plan-coverage based on the user's plan composition:
 *   - Only MAPP plans  → /plan-coverage-mapp
 *   - Only LPP plans   → /plan-coverage-single-cert-policy
 *   - Dual plans       → allow through to the plan-coverage overview
 * Uses PlanDetail.isMultiCertPolicy (the same signal every other plan-coverage component uses),
 * not dealer-code membership. Fails open (returns true) if $planDetails is unavailable — e.g. the
 * client doesn't have Global_Bulk_PlanDetails_Enabled/Global_Get_All_PlanDetails_Enabled turned on
 * yet, so $planDetails never populates. That means only-MAPP/only-LPP users on such a client won't
 * get redirected until their client is on one of those pipelines — same tradeoff as the previous
 * dealer-code version already accepted for a missing/empty $plans or CMS setting.
 */
export const PlanCoverageRedirectGuard: CanActivateFn = async (): Promise<boolean | UrlTree> => {
  const router = inject(Router);
  const loggedInUserSettings = inject(LoggedInUserSettings);

  const planDetails = await firstValueFrom(
    loggedInUserSettings.$planDetails.pipe(
      filter((d): d is PlanDetail[] => d !== null && d.length > 0),
      take(1),
      timeout(PLAN_DETAILS_TIMEOUT_MS),
      catchError(() => of(null))
    )
  );

  if (!planDetails) {
    return true;
  }

  const hasMappPlan = planDetails.some((pd) => pd.isMultiCertPolicy === true);
  const hasLppPlan = planDetails.some((pd) => pd.isMultiCertPolicy !== true);

  if (!hasMappPlan) {
    return router.createUrlTree(['/plan-coverage-single-cert-policy']);
  }

  if (!hasLppPlan) {
    return router.createUrlTree(['/plan-coverage-mapp']);
  }

  return true;
};
